
import Vue from "vue"
import Component from "vue-class-component"
import {Inject} from "vue-property-decorator"
import {AuthenticationService, MFA_REQUIRED_MESSAGE, MfaType} from "@/AuthenticationService"
import Password from "vue-password-strength-meter"
import {AxiosError} from "axios"

/**
 * ChangePassword — inline change-password form component.
 *
 * Emits:
 *   password-changed — password was successfully updated.
 *   cancel           — user cancelled / closed the form.
 */
@Component({
  components: {Password},
})
export default class ChangePassword extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  // ── Form data ─────────────────────────────────────────────────────────────
  oldPassword = ""
  newPassword = ""
  retypeNewPassword = ""
  mfaCode = ""
  mfaChallenged = false

  isPlainTextOldPassword: boolean = <any>null
  isPlainTextNewPassword: boolean = <any>null
  isPlainTextConfirmation: boolean = <any>null

  // ── UI state ──────────────────────────────────────────────────────────────
  passwordStrengthScore = 0
  message = ""
  user: User | null = null

  async created() {
    this.user = await this.authenticationService.loadCurrentUser()
  }

  // ── Computed ──────────────────────────────────────────────────────────────
  get isMfaRequired(): boolean {
    return this.user?.mfaType === MfaType.AtTotp || this.mfaChallenged
  }

  get passwordsMatch(): boolean {
    return this.newPassword === this.retypeNewPassword
  }

  // ── Actions ───────────────────────────────────────────────────────────────
  setPasswordStrengthScore(score: number) {
    this.passwordStrengthScore = score
  }

  isFormValid(): boolean {
    if (this.oldPassword === "" || this.newPassword === "" || this.retypeNewPassword === "") {
      this.message = "All fields must be completed"
      return false
    }

    if (this.newPassword !== this.retypeNewPassword) {
      this.message = "New password doesn't match"
      return false
    }

    if (/\s/.test(this.newPassword)) {
      this.message = "Password can't contain white space"
      return false
    }

    if (this.isMfaRequired && !/^\d{6}$/.test(this.mfaCode)) {
      this.message = "Enter the 6-digit MFA code"
      return false
    }

    return true
  }

  async updatePassword(event: Event) {
    event.preventDefault()
    this.message = ""

    if (!this.isFormValid()) return

    this.message = await this.authenticationService
      .updatePassword(
        this.oldPassword,
        this.newPassword,
        this.isMfaRequired ? this.mfaCode : undefined
      )
      .catch((error: AxiosError<string>) => error.response?.data ?? "Unable to change password")

    if (this.message === MFA_REQUIRED_MESSAGE) {
      this.mfaChallenged = true
      this.mfaCode = ""
      this.message = "A verification code has been emailed to you. Enter it to continue."
    }

    if (this.message === "Password changed successfully") {
      this.$emit("password-changed")
    }
  }

  cancel() {
    this.$emit("cancel")
  }
}
